import { useEffect, useRef } from "react";

const MULTI_DIGIT_WAIT_MS = 400;

function isEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    return target.isContentEditable;
}

function optionValue(option: { value: string }): string {
    return String(option.value);
}

function isSubmitControl(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    if (target.tagName === "INPUT" && (target as HTMLInputElement).type === "submit") return true;
    if (target.tagName !== "BUTTON") return false;
    const type = (target as HTMLButtonElement).type;
    return type === "submit";
}

function submitClosestForm(group: HTMLElement): boolean {
    const form = group.closest("form");
    if (!(form instanceof HTMLFormElement)) return false;

    const submitter = form.querySelector<HTMLButtonElement | HTMLInputElement>(
        'button[type="submit"]:not([disabled]), input[type="submit"]:not([disabled])'
    );

    if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) {
        if (typeof form.requestSubmit === "function") {
            form.requestSubmit(submitter);
            return true;
        }
        submitter.click();
        return true;
    }

    if (typeof form.requestSubmit === "function") {
        form.requestSubmit();
        return true;
    }

    return false;
}

interface UseRatingKeyboardArgs {
    options: Array<{ value: string }>;
    onSelect: (value: string) => void;
    enabled?: boolean;
}

export function useRatingKeyboard({ options, onSelect, enabled = true }: UseRatingKeyboardArgs) {
    const groupRef = useRef<HTMLDivElement>(null);
    const bufferRef = useRef("");
    const timeoutRef = useRef<number | null>(null);
    const onSelectRef = useRef(onSelect);
    const optionsRef = useRef(options);

    onSelectRef.current = onSelect;
    optionsRef.current = options;

    useEffect(() => {
        if (!enabled) return;

        const clearTimer = () => {
            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };

        const flushBuffer = () => {
            const typed = bufferRef.current;
            bufferRef.current = "";
            clearTimer();
            if (!typed) return;

            const match = optionsRef.current.find((option) => optionValue(option) === typed);
            if (match) {
                onSelectRef.current(match.value);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.metaKey || event.ctrlKey || event.altKey) return;
            if (event.repeat) return;
            if (isEditableTarget(event.target)) return;

            const group = groupRef.current;
            if (!group) return;

            const ratingGroups = document.querySelectorAll("[data-rating-keyboard]");
            const isFocused = group.contains(document.activeElement);
            if (ratingGroups.length > 1 && !isFocused) return;

            if (event.key === "Enter") {
                if (isSubmitControl(event.target)) return;
                event.preventDefault();
                event.stopPropagation();
                submitClosestForm(group);
                return;
            }

            if (!/^[0-9]$/.test(event.key)) return;

            event.preventDefault();

            const values = optionsRef.current.map(optionValue);
            const next = bufferRef.current + event.key;
            const exact = values.includes(next);
            const longer = values.some((value) => value.startsWith(next) && value.length > next.length);

            if (exact || longer) {
                bufferRef.current = next;
                clearTimer();
                if (exact && !longer) {
                    flushBuffer();
                    return;
                }
                timeoutRef.current = window.setTimeout(flushBuffer, MULTI_DIGIT_WAIT_MS);
                return;
            }

            const last = event.key;
            const lastExact = values.includes(last);
            const lastLonger = values.some((value) => value.startsWith(last) && value.length > last.length);
            bufferRef.current = lastExact || lastLonger ? last : "";
            clearTimer();

            if (lastExact && !lastLonger) {
                flushBuffer();
                return;
            }
            if (lastExact || lastLonger) {
                timeoutRef.current = window.setTimeout(flushBuffer, MULTI_DIGIT_WAIT_MS);
            }
        };

        window.addEventListener("keydown", handleKeyDown, true);
        return () => {
            window.removeEventListener("keydown", handleKeyDown, true);
            clearTimer();
        };
    }, [enabled]);

    return groupRef;
}
